import React, { useState, useLayoutEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import AccountLabel from './AccountLabel';
import Account from './Account';
import { SECTION_LOGIN } from './constants';
import { trackRegistration } from './util';
import track, { trackGA } from '../../services/track';
import AccountContext from '../../services/AccountContext';
import { Button } from 'edcom-ui';

const GOOGLE = 'google';
const FACEBOOK = 'facebook';

const SwitchAccounts = ({
  setSection,
  setAccount,
  setSocialLoginError,
  setIsLoading,
  storedUsers,
  logOut = false,
  setIsOpen,
}) => {
  const [userList, setUserList] = useState([]);
  const [facebookList, setFacebookList] = useState([]);
  const [googleList, setGoogleList] = useState([]);

  const { logout } = useContext(AccountContext);

  const setSwitchCookieAndLogout = email => {
    if (typeof email === 'undefined') email = null;
    if (email) {
      document.cookie =
        'switchAccounts=' + email + '; expires=1 Jan 2988 00:00:11 UTC; path=/';

      const social = email === 'social' ? ' (social)' : '';
      track('My accounts modal', {
        section: 'Responsive',
        method: 'switch accounts link clicked' + social,
      });
      trackGA(
        'modal',
        'Responsive my accounts modal: switch accounts link clicked' + social
      );
    } else {
      // Clicked "log in with different email"
      document.cookie =
        'switchAccounts=0; expires=1 Jan 2988 00:00:11 UTC; path=/';
      track('My accounts modal', {
        section: 'Responsive',
        method: 'log in with different email clicked',
      });
      trackGA(
        'modal',
        'Responsive my accounts modal: log in with different email clicked'
      );
    }

    logout()
      .then(() => {
        setIsLoading(false);
        setIsOpen(false);
      })
      .catch(() => {
        // Logging out client-side failed for some reason, redirect to old logout url
        window.location.href = '/?__logout';
      });
  };

  const onSelectAccount = email => {
    if (logOut) {
      setSwitchCookieAndLogout(email);
    } else {
      setAccount(email);
      trackRegistration('choose account link clicked', 'Choose account link');
      trackRegistration('opened', 'Viewed Registration Form', 'login');
      setSection(SECTION_LOGIN);
    }
  };

  const populateAccounts = localAccounts => {
    const googleAccounts = [];
    const facebookAccounts = [];
    const processedUsers = [];

    if (Array.isArray(localAccounts)) {
      // Only show the first 5 accounts (ordered by last login date)
      localAccounts = localAccounts.slice(0, 5);

      localAccounts.forEach(account => {
        const label = <AccountLabel account={account} />;
        if (
          Array.isArray(account.socialSignupTypes) &&
          account.socialSignupTypes.length
        ) {
          account.socialSignupTypes.forEach(socialType => {
            if (socialType === GOOGLE) {
              googleAccounts.push(
                <Account
                  email={account.email}
                  label={label}
                  social={GOOGLE}
                  chooseAccount={onSelectAccount}
                  key={account.email}
                  setSocialLoginError={setSocialLoginError}
                  setSection={setSection}
                  setIsLoading={setIsLoading}
                  logOut={logOut}
                  setIsOpen={setIsOpen}
                />
              );
            } else if (socialType === FACEBOOK) {
              facebookAccounts.push(
                <Account
                  email={account.email}
                  label={label}
                  social={FACEBOOK}
                  chooseAccount={onSelectAccount}
                  key={account.email}
                  setSocialLoginError={setSocialLoginError}
                  setSection={setSection}
                  setIsLoading={setIsLoading}
                  logOut={logOut}
                  setIsOpen={setIsOpen}
                />
              );
            }
          });
        }

        if (
          !account.socialSignupTypes ||
          account.socialSignupTypes.length === 0
        ) {
          processedUsers.push(
            <Account
              email={account.email}
              label={label}
              social=""
              chooseAccount={onSelectAccount}
              key={account.email}
              setSocialLoginError={setSocialLoginError}
              setSection={setSection}
              setIsLoading={setIsLoading}
              logOut={logOut}
              setIsOpen={setIsOpen}
            />
          );
        }
      });
    }

    setUserList(processedUsers);
    setFacebookList(facebookAccounts);
    setGoogleList(googleAccounts);
  };

  useLayoutEffect(() => {
    function populateAccountsOrReturnToLogin() {
      if (storedUsers.length) {
        populateAccounts(storedUsers);
      } else {
        trackRegistration('opened', 'Viewed Registration Form', 'login');
        setSection(SECTION_LOGIN);
      }
    }

    populateAccountsOrReturnToLogin();
  }, []);

  // RENDER
  return (
    <div className="choose-account">
      <h3>Choose an Account to Log In</h3>
      <div className="accounts">
        {userList}
        <div
          className={clsx(
            'account-container google-accounts ',
            googleList.length === 0 && 'hidden'
          )}
        >
          <div className="account-container-header loginwith smaller-social-btns">
            Google accounts
          </div>
          <div
            className="register-with-google g-signin2"
            data-width="480"
            data-height="40"
            data-longtitle="true"
            data-scope="profile email"
            data-theme="light"
            data-onsuccess="googleSignIn"
            data-onfailure="googleError"
            data-type="login"
          />
          {googleList}
        </div>
        <div
          className={clsx(
            'account-container facebook-accounts ',
            facebookList.length === 0 && 'hidden'
          )}
        >
          <div className="account-container-header loginwith smaller-social-btns">
            Facebook accounts
          </div>
          {facebookList}
        </div>
      </div>
      <Button
        display="block"
        color="secondary"
        onClick={() => {
          if (logOut) {
            onSelectAccount();
            return;
          }
          setAccount('');
          trackRegistration('opened', 'Viewed Registration Form', 'login');
          trackRegistration(
            'skip choose account link clicked',
            'Skip choose account link'
          );
          setSection(SECTION_LOGIN);
        }}
      >
        Log in with different email
      </Button>
      <div className="cs-disclaimer">
        For more assistance contact{' '}
        <a
          href="http://support.education.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          customer service
        </a>
      </div>
    </div>
  );
};

SwitchAccounts.propTypes = {
  storedUsers: PropTypes.array.isRequired,
  logOut: PropTypes.bool,
  setSection: PropTypes.func.isRequired,
  setAccount: PropTypes.func.isRequired,
  setSocialLoginError: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

export default SwitchAccounts;
